import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchPackages, fetchServices, submitInquiry } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";

const COUNTRIES = ["India", "United States", "United Kingdom", "United Arab Emirates", "Canada", "Australia", "Singapore", "Other"];

export default function InquiryForm({ compact = false, presetPackageId = null }) {
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "India",
    city: "",
    travel_start: "",
    travel_end: "",
    guests: 2,
    package_id: presetPackageId || "",
    services: [],
    message: "",
  });

  useEffect(() => {
    fetchPackages().then(setPackages).catch(() => {});
    fetchServices().then(setServices).catch(() => {});
  }, []);

  useEffect(() => {
    if (presetPackageId) setForm((f) => ({ ...f, package_id: presetPackageId }));
  }, [presetPackageId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (id) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(id) ? f.services.filter((s) => s !== id) : [...f.services, id],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please share your name, email and phone.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = { ...form, guests: Number(form.guests) || 1 };
      if (!payload.package_id) delete payload.package_id;
      await submitInquiry(payload);
      setDone(true);
      toast.success("Your inquiry is received. Our concierge will reach out within 24 hours.");
    } catch {
      toast.error("Could not submit. Please retry or write to concierge@simhastha2028.com");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="inquiry-success"
        className="flex flex-col items-center justify-center gap-6 border hairline bg-secondary/40 p-10 text-center md:p-16"
      >
        <CheckCircle2 className="text-primary" size={48} strokeWidth={1.25} />
        <h3 className="font-serif text-3xl md:text-4xl">Namaste, {form.full_name.split(" ")[0]}.</h3>
        <p className="font-sans-lux max-w-md text-foreground/70">
          Your intent has been received. A dedicated concierge will reach out within 24 hours to
          begin curating your Simhastha journey.
        </p>
        <p className="overline text-primary">Reference · {form.email}</p>
      </div>
    );
  }

  return (
    <form
      data-testid="inquiry-form"
      onSubmit={onSubmit}
      className={`grid gap-8 ${compact ? "" : "md:gap-12"}`}
    >
      <div className="grid gap-8 md:grid-cols-2">
        <Field label="Full name">
          <input data-testid="inquiry-name" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="lux-input w-full text-lg" />
        </Field>
        <Field label="Email">
          <input data-testid="inquiry-email" required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="lux-input w-full text-lg" />
        </Field>
        <Field label="Phone / WhatsApp">
          <input data-testid="inquiry-phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className="lux-input w-full text-lg" placeholder="+91 ..." />
        </Field>
        <Field label="Country of residence">
          <Select value={form.country} onValueChange={(v) => update("country", v)}>
            <SelectTrigger data-testid="inquiry-country" className="lux-input w-full text-lg justify-between">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="border-border bg-secondary">
              {COUNTRIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="City">
          <input data-testid="inquiry-city" value={form.city} onChange={(e) => update("city", e.target.value)} className="lux-input w-full text-lg" />
        </Field>
        <Field label="Guests">
          <input data-testid="inquiry-guests" type="number" min={1} max={40} value={form.guests} onChange={(e) => update("guests", e.target.value)} className="lux-input w-full text-lg" />
        </Field>
        <Field label="Arrival">
          <input data-testid="inquiry-start" type="date" value={form.travel_start} onChange={(e) => update("travel_start", e.target.value)} className="lux-input w-full text-lg" />
        </Field>
        <Field label="Departure">
          <input data-testid="inquiry-end" type="date" value={form.travel_end} onChange={(e) => update("travel_end", e.target.value)} className="lux-input w-full text-lg" />
        </Field>
      </div>

      {packages.length > 0 && (
        <div>
          <p className="overline mb-4 text-foreground/60">Preferred Package (optional)</p>
          <Select value={form.package_id} onValueChange={(v) => update("package_id", v)}>
            <SelectTrigger data-testid="inquiry-package" className="lux-input w-full text-lg justify-between">
              <SelectValue placeholder="Let concierge recommend" />
            </SelectTrigger>
            <SelectContent className="border-border bg-secondary">
              {packages.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name} · {p.tagline}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {services.length > 0 && (
        <div>
          <p className="overline mb-6 text-foreground/60">Services of interest</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <label
                key={s.id}
                data-testid={`inquiry-service-${s.id}`}
                className="flex cursor-pointer items-start gap-3 border hairline bg-secondary/30 p-4 transition-colors hover:border-primary/60"
              >
                <Checkbox
                  checked={form.services.includes(s.id)}
                  onCheckedChange={() => toggleService(s.id)}
                  className="mt-1 rounded-none border-white/30 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                />
                <div>
                  <p className="font-sans-lux text-sm text-foreground">{s.name}</p>
                  <p className="mt-1 text-xs text-foreground/60">{s.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      <Field label="Anything else we should know">
        <Textarea
          data-testid="inquiry-message"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          rows={4}
          className="rounded-none border-0 border-b border-white/15 bg-transparent px-0 py-3 text-lg focus-visible:border-primary focus-visible:ring-0"
          placeholder="Preferences, dietary needs, mobility, VIP requirements, etc."
        />
      </Field>

      <div className="flex flex-col items-start gap-6 border-t hairline pt-8 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-md font-sans-lux text-sm text-foreground/60">
          Every inquiry is answered personally by a senior concierge within 24 hours.
        </p>
        <Button
          data-testid="inquiry-submit"
          type="submit"
          disabled={submitting}
          className="rounded-none bg-primary px-10 py-7 text-primary-foreground hover:bg-primary/90"
        >
          <span className="overline">{submitting ? "Submitting" : "Begin My Journey"}</span>
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="overline text-foreground/60">{label}</span>
      {children}
    </label>
  );
}
